<template>
  <div class="ei-wrap">
    <!-- ══ MASTER: team inbox — every rep's scanned customer emails ══ -->
    <div v-if="grouped" class="ei-linked">
      <!-- Regular Sales rep, not yet linked → opt-in CTA (their own inbox only; no team bar). -->
      <div v-if="ownGrouped && !linked" class="ei-optin">
        <div class="ei-optin-ico" aria-hidden="true">📧</div>
        <div class="ei-optin-txt">
          <div class="ei-optin-h">Link your Microsoft email</div>
          <p>See recent emails from your customers here — grouped by customer, with an AI-suggested part to quote
             in one click. Read-only, your Inbox only, and you can unlink anytime.</p>
        </div>
        <button type="button" class="ei-btn ei-btn-primary" :disabled="scanning" @click="linkEmail">
          <span v-if="scanning" class="ei-spin" aria-hidden="true"></span>{{ scanning ? 'Connecting…' : 'Allow & link my email' }}
        </button>
        <div v-if="error" class="ei-err">{{ error }}</div>
      </div>
      <template v-else>
      <div class="ei-bar" :class="{ 'ei-bar-team': isMaster }">
        <span class="ei-bar-lbl"><template v-if="isMaster">Team inbox — all reps</template><template v-else>Your customer emails</template> <em>(last 30 days)</em></span>
        <div class="ei-bar-actions">
          <button type="button" class="ei-btn ei-btn-sm" :disabled="teamLoading || refreshing || scanning" @click="onGroupRefresh" :title="isMaster ? 'Rescan all reps + reload' : 'Scan your inbox for new customer emails'">
            <span v-if="refreshing || scanning" class="ei-spin" aria-hidden="true"></span>{{ (refreshing || scanning) ? '' : '↻' }}
          </button>
          <button v-if="isMaster && !linked" type="button" class="ei-btn ei-btn-sm" :disabled="scanning" @click="linkEmail" title="Link your own inbox to the team feed">
            <span v-if="scanning" class="ei-spin" aria-hidden="true"></span>{{ scanning ? '' : '＋ Link mine' }}
          </button>
          <button v-else-if="isMaster" type="button" class="ei-btn ei-btn-sm" :disabled="scanning" @click="scanInbox(false)" title="Scan your own inbox now">
            <span v-if="scanning" class="ei-spin" aria-hidden="true"></span>{{ scanning ? '' : 'Scan mine' }}
          </button>
          <button v-if="ownGrouped && linked" type="button" class="ei-btn ei-btn-sm ei-unlink" @click="unlink" title="Unlink your email">Unlink</button>
        </div>
      </div>

      <!-- The recurring auto-scan controls now live in a popup opened by the ⚙ header icon (see the settings
           modal in the portal below), so the bar stays uncluttered. -->

      <!-- View is driven by the title-bar icons (📥 Inbox / 📁 Saved / 🗑 Trash) next to the dock arrow. This
           small label just shows which non-Inbox view is active (the bar title still reads "Team inbox"). -->
      <div v-if="activeFolder === 'saved' || activeFolder === 'deleted' || activeFolder === 'completed'" class="ei-viewlbl" :class="{ 'ei-viewlbl-done': activeFolder === 'completed' }">
        <span v-if="activeFolder === 'saved'">🗂 Saved emails</span>
        <span v-else-if="activeFolder === 'completed'">✅ Completed</span>
        <span v-else>🗑 Trash</span>
      </div>

      <div v-if="teamLoading" class="ei-loading"><span class="ei-spin" aria-hidden="true"></span> Loading {{ isMaster ? 'team inbox' : 'your emails' }}…</div>
      <div v-else-if="teamError" class="ei-err">{{ teamError }}</div>
      <div v-else class="ei-body">
        <div v-if="!teamActive.length" class="ei-empty">
          <template v-if="activeFolder === 'deleted'">Trash is empty.</template>
          <template v-else-if="activeFolder === 'completed'">No completed emails yet — use an email's ⋮ menu → ✅ Move to Completed.</template>
          <template v-else-if="activeFolder === 'saved'">No saved emails yet — open an email's ⋮ menu → Save to folder.</template>
          <template v-else-if="typeof activeFolder === 'number'">This folder is empty.</template>
          <template v-else-if="isMaster">No customer emails yet — reps' emails appear here as they're scanned.</template>
          <template v-else>No customer emails yet — click ↻ above to scan your inbox.</template>
          <div v-if="teamTableMissing" class="ei-note">Setup needed: run the Email Insights + opt-in tables and enable GraphMail (see IT).</div>
        </div>
        <!-- Grouped by customer: one row per customer (email count + latest). A single CLICK opens a modal listing
             all of that customer's emails (per-email actions live there + in the detail modal). -->
        <ul v-else class="ei-list ei-grouplist">
          <li v-for="g in teamGroups" :key="g.code" class="ei-item ei-group" @click="openCustomerEmails(g)"
              :title="'See all ' + g.count + ' email' + (g.count === 1 ? '' : 's') + ' for ' + g.name">
            <div class="ei-item-top">
              <span class="ei-cust">{{ g.name }} <em>({{ g.code }})</em></span>
              <span v-if="g.unread" class="ei-group-badge">{{ g.unread }} unread</span>
              <span class="ei-date">{{ fmtDate(g.latest) }}</span>
            </div>
            <div class="ei-subj ei-group-sub"><span class="ei-group-count">✉ {{ g.count }} email{{ g.count === 1 ? '' : 's' }}</span><span v-if="isMaster && g.repLabel" class="ei-group-rep" :title="'Assigned sales rep'"> · 👤 {{ g.repLabel }}</span> · {{ g.latestSubject }}</div>
            <button v-if="activeFolder === 'deleted'" type="button" class="ei-kebab ei-restore" @click.stop="restoreGroup(g)" @dblclick.stop title="Restore all these emails to the Inbox" aria-label="Restore all to Inbox">↩</button>
            <button v-else-if="activeFolder === 'completed'" type="button" class="ei-kebab ei-restore" @click.stop="reinboxGroup(g)" @dblclick.stop title="Move all these emails back to the Inbox" aria-label="Move all back to Inbox">↩</button>
            <button v-else type="button" class="ei-kebab" @click.stop="openGroupMenu(g, $event)" @dblclick.stop :title="'Actions for all ' + g.count + ' email' + (g.count === 1 ? '' : 's') + ' from ' + g.name" aria-label="Group actions">⋮</button>
          </li>
        </ul>
      </div>
      </template>
    </div>

    <!-- ══ REP (non-Master): the rep's own inbox (unchanged) ══ -->
    <template v-else>
    <!-- Opt-in (not linked) -->
    <div v-if="!linked" class="ei-optin">
      <div class="ei-optin-ico" aria-hidden="true">📧</div>
      <div class="ei-optin-txt">
        <div class="ei-optin-h">Link your Microsoft email</div>
        <p>See recent emails from your customers here, with an AI-suggested part to quote in one click.
           Read-only, your Inbox only, and you can unlink anytime.</p>
      </div>
      <button type="button" class="ei-btn ei-btn-primary" :disabled="scanning" @click="linkEmail">
        <span v-if="scanning" class="ei-spin" aria-hidden="true"></span>{{ scanning ? 'Connecting…' : 'Allow & link my email' }}
      </button>
      <div v-if="error" class="ei-err">{{ error }}</div>
    </div>

    <!-- Linked -->
    <div v-else class="ei-linked">
      <div class="ei-bar">
        <span class="ei-bar-lbl">Customer emails <em>(last 30 days)</em></span>
        <button type="button" class="ei-btn ei-btn-sm" :disabled="scanning || busy" @click="scanInbox(false)" title="Check for new customer emails">
          <span v-if="scanning" class="ei-spin" aria-hidden="true"></span>{{ scanning ? '' : '↻' }}
        </button>
        <button type="button" class="ei-btn ei-btn-sm ei-unlink" @click="unlink" title="Unlink email">Unlink</button>
      </div>

      <div v-if="scanning" class="ei-loading"><span class="ei-spin" aria-hidden="true"></span> {{ scanMsg || 'Scanning your inbox…' }}</div>
      <div v-else-if="busy" class="ei-loading"><span class="ei-spin" aria-hidden="true"></span> Loading…</div>
      <div v-else-if="error" class="ei-err">{{ error }}</div>

      <div v-else class="ei-body">
        <!-- Never scanned on this account yet -->
        <div v-if="nothingYet" class="ei-empty ei-scan-cta">
          <p>No customer emails yet.</p>
          <button type="button" class="ei-btn ei-btn-primary" @click="scanInbox(false)">🔎 Scan my inbox</button>
          <div v-if="tableMissing" class="ei-note">Setup needed: ask IT to run the Email Insights table (rpt_EmailInsight).</div>
        </div>

        <template v-else>
          <!-- Active -->
          <div v-if="!active.length" class="ei-empty">All caught up — no active emails. ✓</div>
          <ul v-else class="ei-list">
            <li v-for="e in active" :key="e.id" class="ei-item" :class="{ 'ei-unread': e.unread }">
              <div class="ei-item-top">
                <span class="ei-cust" :title="'Matched by ' + (e.matchVia === 'domain' ? 'email domain' : 'customer contact')">
                  {{ e.customerName }} <em>({{ e.customerCode }})</em>
                </span>
                <span class="ei-date">{{ fmtDate(e.received) }}</span>
              </div>
              <div class="ei-subj">{{ e.subject }}</div>
              <div class="ei-from">{{ e.fromName || e.fromAddress }}</div>
              <div class="ei-prev">{{ e.preview }}</div>
              <div class="ei-actions">
                <button v-if="e.partNumber" type="button" class="ei-part-chip" :title="e.reason || 'Open a new quote for this part'" @click="openQuote(e)">
                  🧾 Quote {{ e.partNumber }}
                  <span class="ei-part-desc" v-if="e.partDescription">— {{ e.partDescription }}</span>
                  <em v-if="pct(e)" class="ei-conf" :class="'is-' + confBucket(e)" title="AI match probability">{{ pct(e) }}%</em>
                </button>
                <span v-else class="ei-nomatch">No confident part match</span>
                <button type="button" class="ei-btn ei-btn-sm ei-complete" :disabled="acting[e.id]" @click="markComplete(e)" title="Move to the Completed folder (marks read)">
                  ✅ Move to Completed
                </button>
              </div>
            </li>
          </ul>

          <!-- Completed (green folder) -->
          <div v-if="completed.length" class="ei-completed ei-completed-green">
            <button type="button" class="ei-completed-hdr" @click="showCompleted = !showCompleted">
              <span class="ei-caret">{{ showCompleted ? '▾' : '▸' }}</span> ✅ Completed ({{ completed.length }})
            </button>
            <ul v-if="showCompleted" class="ei-list ei-list-done">
              <li v-for="e in completed" :key="e.id" class="ei-item is-done">
                <div class="ei-item-top">
                  <span class="ei-cust">{{ e.customerName }} <em>({{ e.customerCode }})</em></span>
                  <span class="ei-date">{{ fmtDate(e.received) }}</span>
                </div>
                <div class="ei-subj">{{ e.subject }}</div>
                <div class="ei-actions">
                  <button v-if="e.partNumber" type="button" class="ei-part-chip ei-part-chip-sm" :title="e.reason || 'Open a new quote for this part'" @click="openQuote(e)">
                    🧾 Quote {{ e.partNumber }}
                  </button>
                  <button type="button" class="ei-btn ei-btn-sm ei-reopen" :disabled="acting[e.id]" @click="reopen(e)" title="Move back to the Inbox as unread">
                    ↩ Move to Inbox
                  </button>
                </div>
              </li>
            </ul>
          </div>
        </template>
      </div>
    </div>
    </template>

    <!-- Card footer: view EVERY email (across all customers) in one modal list. -->
    <div v-if="grouped && teamActive.length" class="ei-foot">
      <button type="button" class="ei-btn ei-btn-sm ei-viewall" @click="openAllEmails">📋 View all emails ({{ teamActive.length }})</button>
    </div>

    <!-- Detail + Reply modals (Master). Portaled to the report root so they escape the grid card's CSS
         transform/overflow and overlay the whole report correctly. -->
    <portal to="report-email-modal">
      <!-- Customer emails / View-all list modal (reusable). emailListEmails re-derives from teamActive so row
           actions (complete / delete / file) reflect live. A single CLICK on a row → the detail modal (higher z) on top. -->
      <div v-if="emailList.open" class="ei-modal-list-back" @click="closeEmailList"></div>
      <div v-if="emailList.open" class="ei-modal-list" role="dialog" aria-label="Emails">
        <div class="ei-modal-head">
          <span class="ei-modal-title">{{ emailList.title }} <em>({{ emailListEmails.length }})</em></span>
          <button type="button" class="ei-modal-x" @click="closeEmailList" aria-label="Close">×</button>
        </div>
        <div class="ei-modal-listbody">
          <div v-if="!emailListEmails.length" class="ei-empty">No emails here.</div>
          <ul v-else class="ei-list">
            <li v-for="e in emailListEmails" :key="e.id" class="ei-item ei-team-item" :class="{ 'ei-unread': e.unread }" @click="openDetail(e)" :title="'Open — ' + (e.subject || '')">
              <div class="ei-item-top">
                <span class="ei-cust">{{ e.customerName }} <em>({{ e.customerCode }})</em></span>
                <span v-if="isMaster" class="ei-rep" :title="'Sales rep: ' + e.repEmail">{{ e.repEmail }}</span>
                <span class="ei-date">{{ fmtDate(e.received) }}</span>
              </div>
              <div class="ei-subj">{{ e.subject }}<span v-if="e.folderName" class="ei-folder-tag">📁 {{ e.folderName }}</span></div>
              <button v-if="activeFolder === 'deleted'" type="button" class="ei-kebab ei-restore" @click.stop="restoreEmail(e)" @dblclick.stop title="Restore to Inbox" aria-label="Restore to Inbox">↩</button>
              <button v-else-if="activeFolder === 'completed'" type="button" class="ei-kebab ei-restore" @click.stop="reinboxTeamEmail(e)" @dblclick.stop title="Move back to Inbox as unread" aria-label="Move back to Inbox as unread">↩</button>
              <button v-else type="button" class="ei-kebab" @click.stop="openRowMenu(e, $event)" @dblclick.stop title="Complete, delete, or save to a folder" aria-label="More actions">⋮</button>
            </li>
          </ul>
        </div>
      </div>
      <div v-if="detail" class="ei-modal-back" @click="closeDetail"></div>
      <div v-if="detail" class="ei-modal ei-modal-detail" role="dialog" aria-label="Email details">
        <!-- Prev/next within the open group's email list (newest-first): ‹ = newer, › = older. -->
        <button v-if="detailHasPrev" type="button" class="ei-nav-arrow ei-nav-prev" @click="goDetailPrev" title="Newer email from this customer" aria-label="Newer email">‹</button>
        <button v-if="detailHasNext" type="button" class="ei-nav-arrow ei-nav-next" @click="goDetailNext" title="Older email from this customer" aria-label="Older email">›</button>
        <div class="ei-modal-head">
          <span class="ei-modal-title">{{ detail.customerName }} <em>({{ detail.customerCode }})</em></span>
          <button type="button" class="ei-modal-x" @click="closeDetail" aria-label="Close">×</button>
        </div>
        <div class="ei-modal-meta">
          <div><b>From:</b> {{ detail.fromName || detail.fromAddress }}</div>
          <div v-if="isMaster"><b>Rep:</b> {{ detail.repEmail }}</div>
          <div><b>Date:</b> {{ fmtDate(detail.received) }}</div>
          <div class="ei-modal-subj"><b>Subject:</b> {{ detail.subject }}</div>
        </div>
        <div v-if="detailNotation" class="ei-notation">
          <button type="button" class="ei-notation-lbl ei-notation-toggle" @click.stop="noteOpen = !noteOpen" :aria-expanded="noteOpen ? 'true' : 'false'">
            <span class="ei-notation-caret">{{ noteOpen ? '▾' : '▸' }}</span>📝 Customer Notation
          </button>
          <div v-if="noteOpen" class="ei-notation-txt">{{ detailNotation }}</div>
        </div>
        <div v-if="detailSpecs" class="ei-notation ei-spec">
          <button type="button" class="ei-notation-lbl ei-notation-toggle" @click.stop="specOpen = !specOpen" :aria-expanded="specOpen ? 'true' : 'false'">
            <span class="ei-notation-caret">{{ specOpen ? '▾' : '▸' }}</span>📋 Customer Specifications
          </button>
          <div v-if="specOpen" class="ei-notation-txt">{{ detailSpecs }}</div>
        </div>
        <div v-if="detail && (detailNotation || detailSpecs)" class="ei-persp-wrap">
          <button type="button" class="ei-persp-btn" @click="loadDetailPersp" :disabled="detailPersp.busy"
                  title="AI sales perspective on this customer's notes &amp; specs">💡 AI sales perspective</button>
          <div v-if="detailPersp.open" class="ei-persp">
            <div v-if="detailPersp.busy" class="ei-persp-loading"><span class="ei-persp-spin" aria-hidden="true"></span> Thinking…</div>
            <template v-else>
              <div v-if="detailPersp.headline" class="ei-persp-headline">{{ detailPersp.headline }}</div>
              <div class="ei-persp-body">{{ detailPersp.body }}</div>
              <ul v-if="detailPersp.actions && detailPersp.actions.length" class="ei-persp-actions">
                <li v-for="(a, ai) in detailPersp.actions" :key="ai">{{ a }}</li>
              </ul>
            </template>
          </div>
        </div>
        <div class="ei-modal-body">
          <div v-if="detailLoading" class="ei-loading"><span class="ei-spin" aria-hidden="true"></span> Loading…</div>
          <pre v-else class="ei-modal-text">{{ detailBody || '(no message body captured)' }}</pre>
        </div>
        <div class="ei-modal-ai">
          <template v-if="detailSuggestions.length">
            <div class="ei-ai-h">AI-suggested part{{ detailSuggestions.length > 1 ? 's' : '' }}<em v-if="detailSuggestions.length > 1"> — ranked, most likely first</em></div>
            <div v-for="(s, i) in detailSuggestions" :key="i" class="ei-ai-sugg">
              <button type="button" class="ei-part-chip" :title="s.reason || 'Open a new quote for this part'" @click="openQuoteFor(s)">
                🧾 Quote {{ s.partNumber }}
                <span class="ei-part-desc" v-if="s.partDescription">— {{ s.partDescription }}</span>
                <em v-if="pct(s)" class="ei-conf" :class="'is-' + confBucket(s)">{{ pct(s) }}%</em>
              </button>
              <div v-if="s.machineName || s.assembly" class="ei-ai-ctx">{{ s.machineName }}<span v-if="s.assembly"> · {{ s.assembly }}</span></div>
              <div v-if="s.reason" class="ei-ai-reason">{{ s.reason }}</div>
            </div>
          </template>
          <span v-else class="ei-nomatch">No confident part match.</span>
        </div>
        <div class="ei-modal-foot">
          <button type="button" class="ei-btn" @click="closeDetail">Close</button>
          <button v-if="isMaster" type="button" class="ei-btn ei-btn-primary" @click="openReply(detail)">↩ Reply</button>
        </div>
      </div>

      <div v-if="reply.open" class="ei-modal-back" @click="closeReply"></div>
      <div v-if="reply.open" class="ei-modal ei-modal-reply" role="dialog" aria-label="Reply">
        <div class="ei-modal-head">
          <span class="ei-modal-title">↩ Reply</span>
          <button type="button" class="ei-modal-x" @click="closeReply" aria-label="Close">×</button>
        </div>
        <div class="ei-modal-body ei-reply-body">
          <label class="ei-lbl">To</label>
          <div class="ei-to-box">
            <span v-for="a in reply.toList" :key="a" class="ei-to-chip" :title="a">{{ toChipLabel(a) }}<button type="button" class="ei-to-x" @click="removeTo(a)" aria-label="Remove recipient">×</button></span>
            <select v-if="toAvailable.length" class="ei-to-add" @change="onAddSelect($event)">
              <option value="">+ Add recipient…</option>
              <option value="__ALL__">✓ All recipients ({{ toAvailable.length }})</option>
              <option v-for="o in toAvailable" :key="o.address" :value="o.address">{{ o.label }}</option>
            </select>
            <span v-if="!reply.toList.length" class="ei-to-empty">Pick at least one recipient</span>
          </div>
          <label class="ei-lbl">Cc <span class="ei-lbl-opt">(optional — add anyone else)</span></label>
          <input class="ei-input" v-model="reply.cc" placeholder="email@company.com, email2@company.com" />
          <label class="ei-lbl">Subject</label>
          <input class="ei-input" v-model="reply.subject" />
          <label class="ei-lbl">Message</label>
          <textarea class="ei-input ei-textarea" v-model="reply.body" rows="7" placeholder="Type your message…"></textarea>
          <div class="ei-reply-hint">Sent from parts@jcsteele.com; replies come back to the rep.</div>
          <div v-if="reply.msg" class="ei-note">{{ reply.msg }}</div>
        </div>
        <div class="ei-modal-foot">
          <button type="button" class="ei-btn" @click="closeReply" :disabled="!!reply.busy">Cancel</button>
          <button type="button" class="ei-btn ei-btn-primary" @click="sendReply" :disabled="!!reply.busy || !reply.toList.length || !reply.subject || !reply.body">
            <span v-if="reply.busy" class="ei-spin" aria-hidden="true"></span>{{ reply.busy ? 'Sending…' : 'Send' }}
          </button>
        </div>
      </div>

      <!-- Per-email actions menu (⋮): delete or save-to-folder. Fixed-positioned near the row's button; portaled
           here so it isn't clipped by the grid card's overflow/stacking context. -->
      <div v-if="rowMenu.open" class="ei-menu-back" @click="closeRowMenu"></div>
      <div v-if="rowMenu.open" class="ei-menu" :style="{ left: rowMenu.x + 'px', top: rowMenu.y + 'px' }" @click.stop>
        <template v-if="rowMenu.view === 'root'">
          <div class="ei-menu-hd">{{ rowMenu.emails ? (rowMenu.groupName + ' — ' + rowMenu.emails.length + ' email' + (rowMenu.emails.length === 1 ? '' : 's')) : ((rowMenu.email && rowMenu.email.customerName) || 'Email') }}</div>
          <button type="button" class="ei-menu-item ei-menu-done" @click="menuComplete()">✅ {{ rowMenu.emails ? 'Move all to Completed' : 'Move to Completed' }}</button>
          <button type="button" class="ei-menu-item" @click="menuGoFolders">📁 {{ rowMenu.emails ? 'Save all to folder…' : 'Save to folder…' }}</button>
          <button type="button" class="ei-menu-item ei-menu-del" @click="rowMenu.view = 'confirmdel'">🗑 {{ rowMenu.emails ? 'Delete all' : 'Delete' }}</button>
        </template>
        <template v-else-if="rowMenu.view === 'folders'">
          <div class="ei-menu-hd"><button type="button" class="ei-menu-crumb" @click="rowMenu.view = 'root'">‹</button> Save to…</div>
          <div class="ei-menu-scroll">
            <button v-for="f in folders" :key="f.id" type="button" class="ei-menu-item" @click="fileTo(f.id)">📁 {{ f.name }} <em class="ei-menu-ct">{{ f.count }}</em></button>
            <div v-if="!folders.length" class="ei-menu-empty">No folders yet.</div>
          </div>
          <button type="button" class="ei-menu-item ei-menu-new" @click="menuGoNewFolder">➕ New folder…</button>
        </template>
        <template v-else-if="rowMenu.view === 'newfolder'">
          <div class="ei-menu-hd"><button type="button" class="ei-menu-crumb" @click="rowMenu.view = 'folders'">‹</button> New folder</div>
          <input ref="newFolderInput" class="ei-menu-input" v-model="rowMenu.newName" maxlength="200" placeholder="Folder name" @keyup.enter="createAndFile" @click.stop />
          <button type="button" class="ei-menu-item ei-menu-go" :disabled="!rowMenu.newName.trim() || rowMenu.busy" @click="createAndFile">
            <span v-if="rowMenu.busy" class="ei-spin" aria-hidden="true"></span>Create &amp; save here
          </button>
        </template>
        <template v-else-if="rowMenu.view === 'confirmdel'">
          <div class="ei-menu-hd">{{ rowMenu.emails ? ('Delete all ' + rowMenu.emails.length + ' emails?') : 'Delete this email?' }}</div>
          <div class="ei-menu-note"><template v-if="isMaster">Removes {{ rowMenu.emails ? 'them' : 'it' }} from your team inbox (the reps' copies stay untouched).</template><template v-else>Removes {{ rowMenu.emails ? 'them' : 'it' }} from your inbox.</template></div>
          <div class="ei-menu-row">
            <button type="button" class="ei-menu-item" @click="rowMenu.view = 'root'">Cancel</button>
            <button type="button" class="ei-menu-item ei-menu-del" :disabled="rowMenu.busy" @click="doDelete">
              <span v-if="rowMenu.busy" class="ei-spin" aria-hidden="true"></span>Delete
            </button>
          </div>
        </template>
      </div>

      <!-- Auto-scan settings (Master) — opened by the ⚙ header icon next to the Inbox icon. Small centered modal
           with the recurring-scan on/off + a 15-minute-increment interval. -->
      <div v-if="scanSettingsOpen" class="ei-modal-back" @click="scanSettingsOpen = false"></div>
      <div v-if="scanSettingsOpen" class="ei-settings" role="dialog" aria-label="Auto-scan settings">
        <div class="ei-modal-head">
          <span class="ei-modal-title">⚙ Auto-scan settings</span>
          <button type="button" class="ei-modal-x" @click="scanSettingsOpen = false" aria-label="Close">×</button>
        </div>
        <div class="ei-settings-body">
          <p class="ei-settings-lead">Automatically rescan all reps' inboxes on a schedule — no one has to open the report.</p>
          <div class="ei-autoscan">
            <label class="ei-autoscan-tog">
              <input type="checkbox" :checked="autoScan.enabled" @change="setAutoScan($event.target.checked, autoScan.intervalMinutes)" /> Auto-scan every
            </label>
            <select class="ei-autoscan-int" :value="autoScan.intervalMinutes" :disabled="!autoScan.enabled" @change="setAutoScan(autoScan.enabled, Number($event.target.value))">
              <option v-for="m in intervalOptions" :key="m" :value="m">{{ fmtInterval(m) }}</option>
            </select>
          </div>
          <div v-if="!autoScan.graphEnabled" class="ei-settings-note">⚠ Server-side scanning isn't enabled yet — ask IT to turn on GraphMail. Your setting is saved and takes effect as soon as it's enabled.</div>
        </div>
      </div>
    </portal>
  </div>
</template>

<script>
import httpClient from "@/assets/js/HttpClient";
import { fetchRecentInbox, hasMailConsentSilently } from "@/assets/js/msalAuth";

// Email Insights card (Sales Insights). DB-backed so the inbox is pulled ONCE, each part suggestion is
// computed ONCE, and a rep can Mark-as-Complete (archive) / Reopen an email:
//   • mount → load persisted insights from the DB (/user/email-insights) — no Graph call, instant.
//   • if linked + nothing saved yet + consent already granted → a single silent one-time scan (per session).
//   • the ↻ button = manual "check for new" (only analyzes emails not already saved).
// A scan reads recent Inbox via Microsoft Graph (delegated Mail.Read, msalAuth), cross-references participants
// with customer companies (/user/email-customer-match — portal user OR email domain), asks the AI to align each
// NEW matched email to one of that customer's previously-ordered parts (/report/email-part-align), and persists
// the batch (/user/email-insights). Clicking a suggested part opens a NEW TAB that switches into the customer
// company and starts a quote on Search.
export default {
  name: "EmailInsights",
  props: {
    // Opt-in state, persisted by the parent in the per-user report-dashboard blob.
    linked: { type: Boolean, default: false },
    // Master AND Sales All see the TEAM inbox (all reps' scanned emails) instead of just their own list.
    isMaster: { type: Boolean, default: false },
    // ⚙ auto-scan settings (the server-wide scan schedule) — Master only. Sales All gets the team view but
    // not the global scan control, so this stays false for them.
    canAutoScan: { type: Boolean, default: false },
    // Regular 'Sales' rep: show the SAME grouped view + folder buttons as the team, but the backend self-scopes
    // the list to their OWN scanned emails only (they never see other reps'). Master/Sales All use isMaster.
    ownGrouped: { type: Boolean, default: false },
  },
  data() {
    return {
      busy: false,        // loading saved insights from the DB
      scanning: false,    // running a Graph scan
      scanMsg: "",
      error: "",
      active: [],         // saved insights, status = active
      completed: [],      // saved insights, status = complete
      acting: {},         // rowId -> true while a complete/reopen is in flight
      showCompleted: false,
      tableMissing: false,
      // ── Master team-inbox mode ──
      teamActive: [],         // all reps' active emails (aggregated)
      teamLoading: false,
      teamError: "",
      teamTableMissing: false,
      refreshing: false,      // server rescan in flight
      detail: null,           // the email row whose details modal is open
      detailBody: "",         // full body for the open modal (fetched on open)
      detailNotation: "",     // the matched customer's VMFG notation (shown atop the open modal)
      noteOpen: false, specOpen: false,   // Customer Notation / Specifications collapsed by default (click to expand)
      detailSpecs: "",        // the matched customer's VMFG specifications (📋 block in the open modal)
      detailPersp: { open: false, busy: false, headline: "", body: "", actions: [] },   // 💡 inline AI perspective
      detailLoading: false,
      detailSuggestions: [],  // the ranked AI part suggestions for the open email (top + alternates)
      reply: { open: false, insightId: 0, repEmail: "", toList: [], cc: "", toOptions: [], subject: "", body: "", busy: false, msg: "" },
      // ── Folders + per-email actions (Master) ──
      folders: [],            // this Master's folders: [{ id, name, count }]
      activeFolder: "inbox",  // which view is showing: "inbox" (un-filed), "all", "completed", "deleted", or a folder id
      rowMenu: { open: false, email: null, emails: null, groupName: "", x: 0, y: 0, view: "root", newName: "", busy: false },  // ⋮ menu (emails set = whole-customer group mode)
      // ── Master-controlled recurring auto-scan of all reps' inboxes (server-side TeamInboxScanner) ──
      autoScan: { enabled: false, intervalMinutes: 15, graphEnabled: false, busy: false },
      scanSettingsOpen: false,   // the ⚙ auto-scan settings popup
      // Customer-grouped email-list modal (double-click a customer group, or the footer "View all emails").
      // `code` = which customer to show (null = every email); rows re-derive via emailListEmails.
      emailList: { open: false, title: "", code: null },
    };
  },
  computed: {
    nothingYet() { return !this.active.length && !this.completed.length; },
    // The grouped, folder-organized view (list + folders/trash + modals) is shown to Master, Sales All (team-wide)
    // AND a regular Sales rep (own emails only, via ownGrouped). isMaster stays the "team-wide / all reps" flag.
    grouped() { return this.isMaster || this.ownGrouped; },
    // 15-minute blocks from 15 min up to 8 hr for the auto-scan interval picker.
    intervalOptions() {
      var opts = [];
      for (var m = 15; m <= 480; m += 15) opts.push(m);
      return opts;
    },
    // Team inbox grouped by customer — one entry per customer code with its email count, unread count, and the
    // latest email's date/subject (newest-first). Drives the grouped list; double-clicking a group opens the modal.
    teamGroups() {
      var m = {};
      (this.teamActive || []).forEach(function (e) {
        var code = e.customerCode || "(unmatched)";
        if (!m[code]) m[code] = { code: code, name: e.customerName || "Unmatched", count: 0, unread: 0, latest: null, latestSubject: "", reps: {} };
        var g = m[code];
        g.count++;
        if (e.unread) g.unread++;
        var t = e.received ? new Date(e.received).getTime() : 0;
        var cur = g.latest ? new Date(g.latest).getTime() : -1;
        if (t > cur) { g.latest = e.received; g.latestSubject = e.subject || ""; }
        // Tally the assigned sales rep(s) whose inbox these emails were scanned from, so the group
        // row can show the rep WITHOUT the user having to open the customer (usually one per customer).
        var rn = (e.repName || e.repEmail || "").trim();
        if (rn) g.reps[rn] = (g.reps[rn] || 0) + 1;
      });
      return Object.keys(m).map(function (k) {
        var g = m[k];
        // Most-frequent rep first; join with commas if a customer's emails span >1 rep.
        g.repLabel = Object.keys(g.reps).sort(function (a, b) { return g.reps[b] - g.reps[a]; }).join(", ");
        return g;
      }).sort(function (a, b) { return (b.latest ? new Date(b.latest).getTime() : 0) - (a.latest ? new Date(a.latest).getTime() : 0); });
    },
    // Emails shown in the list modal — re-derived from teamActive so row actions (complete/delete/file) reflect
    // live. code=null → every email (footer "View all"); else that one customer.
    emailListEmails() {
      if (!this.emailList.open) return [];
      var code = this.emailList.code;
      if (!code) return this.teamActive || [];
      return (this.teamActive || []).filter(function (e) { return (e.customerCode || "(unmatched)") === code; });
    },
    // Position of the open detail email within the current group list (newest-first) — drives the ‹ › nav arrows.
    detailIndex() {
      if (!this.detail) return -1;
      var id = this.detail.id, list = this.emailListEmails || [];
      for (var i = 0; i < list.length; i++) { if (list[i].id === id) return i; }
      return -1;
    },
    detailHasPrev() { return this.detailIndex > 0; },                                             // ‹ newer
    detailHasNext() { var i = this.detailIndex; return i >= 0 && i < (this.emailListEmails || []).length - 1; },  // › older
    // Reply "To" picker: the chain recipients NOT already added as chips (drives the dropdown; picked ones leave it).
    toAvailable() {
      var sel = this.reply.toList || [];
      return (this.reply.toOptions || []).filter(function (o) { return sel.indexOf(o.address) < 0; });
    },
  },
  mounted() {
    // Master: also load the aggregated team inbox (all reps' scanned emails) + this Master's folders, and listen
    // for the header 📁 Saved / 🗑 Trash shortcuts (emitted by CustomerInsights next to the dock arrow).
    if (this.isMaster) { this.loadTeam(); this.loadFolders(); if (this.canAutoScan) this.loadScanSetting(); this.$root.$on("report-email-view", this.onOpenView); }
    // Regular Sales rep: same grouped view, but self-scoped to their own emails. Load only once linked; still
    // listen for the header 📥 📁 ✅ 🗑 shortcuts so the buttons work.
    else if (this.ownGrouped) { this.loadFolders(); if (this.linked) this.loadTeam(); this.$root.$on("report-email-view", this.onOpenView); }
    // DB-backed: load saved insights first (no Graph). Then, for an already-consented rep with nothing saved,
    // do ONE silent auto-scan (session-guarded so a zero-match account never re-scans on every mount).
    var self = this;
    this.loadFromDb().then(function () {
      if (!self.linked || !self.nothingYet) return;
      if (sessionStorage.getItem("ei_autoscanned")) return;
      hasMailConsentSilently().then(function (ok) {
        if (ok) { sessionStorage.setItem("ei_autoscanned", "1"); self.scanInbox(false); }
      }).catch(function () { /* no silent consent → wait for the Scan button */ });
    });
  },
  beforeDestroy() {
    this.$root.$off("report-email-view", this.onOpenView);
  },
  methods: {
    fmtDate(iso) {
      try { return new Date(iso).toLocaleDateString(); } catch (e) { return ""; }
    },
    // Open the list modal for one customer group (double-click) or every email (footer "View all emails").
    openCustomerEmails(g) {
      if (!g) return;
      this.emailList = { open: true, title: g.name + " (" + g.code + ")", code: g.code };
    },
    openAllEmails() { this.emailList = { open: true, title: "All emails", code: null }; },
    closeEmailList() { this.emailList = { open: false, title: "", code: null }; },
    // Match probability as a 0-100 number. Prefer the AI's numeric probability; fall back to the coarse
    // confidence bucket for rows scanned before the numeric probability existed.
    pct(e) {
      if (e && typeof e.probability === "number" && e.probability > 0) return e.probability;
      var c = e && e.confidence;
      if (c === "high") return 85;
      if (c === "medium") return 50;
      if (c === "low") return 25;
      return 0;
    },
    confBucket(e) {
      if (e && e.confidence) return e.confidence;
      var p = this.pct(e);
      return p >= 70 ? "high" : (p >= 40 ? "medium" : "low");
    },
    // Load the rep's saved insights from the DB and split into active / completed.
    loadFromDb() {
      var self = this;
      this.busy = true; this.error = "";
      return httpClient.get("/user/email-insights")
        .then(function (res) {
          var list = (res.data && res.data.insights) || [];
          self.active = list.filter(function (r) { return r.status !== "complete"; });
          self.completed = list.filter(function (r) { return r.status === "complete"; });
          self.tableMissing = !!(res.data && res.data.tableMissing);
        })
        .catch(function () { /* leave current lists as-is */ })
        .finally(function () { self.busy = false; });
    },
    // First-time link: triggers the MSAL consent popup (inside the scan), marks opted-in, then scans + persists.
    linkEmail() { this.scanInbox(true); },
    // Scan the inbox for NEW customer emails, AI-align them, persist, and reload from the DB.
    scanInbox(isLink) {
      var self = this;
      if (this.scanning) return;
      this.error = ""; this.scanning = true; this.scanMsg = "Scanning your inbox…";
      fetchRecentInbox(30, 50)
        .then(function (msgs) {
          if (isLink && !self.linked) { self.$emit("set-linked", true); self.optin(true); }
          return self.matchAndAlign(msgs || []);
        })
        .then(function (items) {
          if (!items.length) { self.scanMsg = "No new customer emails found."; return null; }
          self.scanMsg = "Saving…";
          return httpClient.post("/user/email-insights", { Items: items });
        })
        .then(function () { return self.loadFromDb(); })
        .then(function () { if (self.isMaster || self.ownGrouped) { self.loadTeam(); self.loadFolders(); } })   // my own scan feeds the (self-scoped) grouped list too
        .catch(function (err) { self.error = self._msg(err); })
        .finally(function () { self.scanning = false; self.scanMsg = ""; });
    },
    // From a batch of Graph messages: collect participant addresses, ask the backend which map to a customer,
    // keep only NEW matched emails (not already saved), AI-align each, and shape the rows to persist.
    matchAndAlign(msgs) {
      var self = this;
      var addrs = {};
      msgs.forEach(function (e) {
        [e.from].concat(e.to || []).concat(e.cc || []).forEach(function (p) {
          if (p && p.address && p.address.indexOf("@jcsteele.com") < 0) addrs[p.address] = 1;
        });
      });
      var list = Object.keys(addrs);
      if (!list.length) return Promise.resolve([]);

      var known = {};
      this.active.concat(this.completed).forEach(function (r) { if (r.emailId) known[r.emailId] = 1; });

      return httpClient.post("/user/email-customer-match", { Addresses: list })
        .then(function (res) {
          var m = (res.data && res.data.matches) || {};
          var newMatched = [];
          msgs.forEach(function (e) {
            if (known[e.id]) return;   // already saved → don't re-analyze (recommend once)
            var parts = [e.from].concat(e.to || []).concat(e.cc || []);
            var hit = null;
            for (var i = 0; i < parts.length; i++) {
              var a = parts[i] && parts[i].address;
              if (a && m[a]) { hit = m[a]; break; }
            }
            if (hit) newMatched.push({ e: e, cust: hit });
          });
          if (!newMatched.length) return [];

          self.scanMsg = "Analyzing " + newMatched.length + " email" + (newMatched.length === 1 ? "" : "s") + "…";
          return Promise.all(newMatched.map(function (nm) {
            return httpClient.post("/report/email-part-align", {
              CustomerCode: nm.cust.customerCode,
              Subject: nm.e.subject,
              Body: nm.e.body || nm.e.preview,
            })
              .then(function (r) { return { nm: nm, match: (r.data && r.data.match) || null, matches: (r.data && r.data.matches) || [] }; })
              .catch(function () { return { nm: nm, match: null, matches: [] }; });
          })).then(function (results) {
            return results.map(function (x) {
              var e = x.nm.e, c = x.nm.cust, mm = x.match, mlist = x.matches || [];
              var item = {
                EmailId: e.id,
                Subject: e.subject,
                FromName: e.from && e.from.name,
                FromAddress: e.from && e.from.address,
                Received: e.received,
                Preview: e.preview,
                Body: self._plain(e.body || e.preview),   // full message (plain text) for the Master team modal
                CustomerCode: c.customerCode,
                CustomerName: c.customerName,
                CompanyId: c.companyId,
                MatchVia: c.via,
              };
              if (mm) {
                item.PartNumber = mm.partNumber;
                item.PartDescription = mm.partDescription;
                item.MachineName = mm.machineName;
                item.Assembly = mm.assembly;
                item.AssemblyId = mm.assemblyId;
                item.AvgQty = mm.avgQty;
                item.Probability = mm.probability;
                item.Confidence = mm.confidence;
                item.Reason = mm.reason;
              }
              // Persist the FULL ranked list (top + alternates) so the details modal can show more than one.
              if (mlist && mlist.length) item.Suggestions = JSON.stringify(mlist);
              // Capture the chain participants (From/To/CC, name+address) so the Reply picker can list everyone.
              var parts = [].concat(
                e.from ? [{ n: e.from.name || "", a: e.from.address || "", r: "from" }] : [],
                (e.to || []).map(function (p) { return { n: (p && p.name) || "", a: (p && p.address) || "", r: "to" }; }),
                (e.cc || []).map(function (p) { return { n: (p && p.name) || "", a: (p && p.address) || "", r: "cc" }; })
              ).filter(function (p) { return p.a; });
              if (parts.length) item.Participants = JSON.stringify(parts);
              return item;
            });
          });
        });
    },
    markComplete(e) {
      var self = this;
      this.$set(this.acting, e.id, true);
      httpClient.post("/user/email-insights/complete", { Id: e.id })
        .then(function () {
          self.active = self.active.filter(function (r) { return r.id !== e.id; });
          e.status = "complete"; e.unread = false;   // completing marks it read
          self.completed.unshift(e);
        })
        .catch(function () { self.error = "Couldn't mark that complete. Please try again."; })
        .finally(function () { self.$delete(self.acting, e.id); });
    },
    reopen(e) {
      var self = this;
      this.$set(this.acting, e.id, true);
      httpClient.post("/user/email-insights/reopen", { Id: e.id })
        .then(function () {
          self.completed = self.completed.filter(function (r) { return r.id !== e.id; });
          e.status = "active"; e.unread = true;   // returns to the Inbox as unread (bold)
          self.active.unshift(e);
          self.active.sort(function (a, b) { return new Date(b.received || 0) - new Date(a.received || 0); });
        })
        .catch(function () { self.error = "Couldn't reopen that email. Please try again."; })
        .finally(function () { self.$delete(self.acting, e.id); });
    },
    openQuote(e) {
      if (!e || !e.partNumber || !e.customerCode) return;
      // Fully synchronous (the catalog machine/assembly were resolved server-side at scan time), so window.open
      // stays inside the click gesture — no popup block. The target CUSTOMER company rides in the URL; the new
      // tab switches into it on load (Head.getCompanies honors switchCompanyId) so the quote is placed under the
      // customer and survives the fresh page load. The report tab is left untouched.
      var q = { code: e.partNumber, allParts: "true", intent: "quote" };
      if (e.machineName) q.machineName = e.machineName;
      if (e.assembly) q.assembly = e.assembly;
      if (e.assemblyId) q.assemblyId = e.assemblyId;
      // Exact deep-link ids (resolved server-side against the customer's ACCOUNT): machineSel is the Search
      // machine dropdown's own value space (CompanyMachine.Id), subAssembly/-Id picks the sub-assembly card.
      // Search resolves these id-first and falls back to the name matching above for older stored rows.
      if (e.companyMachineId) q.machineSel = e.companyMachineId;
      if (e.subAssembly) q.subAssembly = e.subAssembly;
      if (e.subAssemblyId) q.subAssemblyId = e.subAssemblyId;
      if (e.partDescription) q.partDesc = e.partDescription;
      if (e.avgQty) q.avgQty = e.avgQty;
      if (e.reason) q.reason = e.reason;                 // AI recap → leads the Request-Parts description
      var _p = this.pct(e); if (_p) q.prob = _p;          // match % → mentioned in the request body
      if (e.companyId) q.switchCompanyId = e.companyId;
      if (e.customerCode) q.switchCompanyCode = e.customerCode;
      if (e.customerName) q.switchCompanyName = e.customerName;
      var href = window.location.origin + this.$router.resolve({ name: "Search", query: q }).href;
      window.open(href, "_blank");
    },
    unlink() {
      // Local view only — saved rows stay in the DB so re-linking restores the history.
      this.$emit("set-linked", false);
      this.optin(false);   // stop server-side scanning of my mailbox
      this.active = []; this.completed = []; this.error = ""; this.scanMsg = "";
    },
    // ── Master team inbox ────────────────────────────────────────────────────────────────────────
    loadTeam() {
      var self = this;
      this.teamLoading = true; this.teamError = "";
      var f = this.activeFolder;
      var q = (f === "all") ? "?folder=all" : (f === "inbox" || f == null) ? "" : ("?folder=" + f);
      return httpClient.get("/report/team-email-insights" + q)
        .then(function (res) {
          var list = (res.data && res.data.insights) || [];
          self.teamActive = list.filter(function (r) { return r.status !== "complete"; });
          self.teamTableMissing = !!(res.data && res.data.tableMissing);
        })
        .catch(function () { self.teamError = "Couldn't load the team inbox."; })
        .finally(function () { self.teamLoading = false; });
    },
    // Load this Master's folders (for the chips + the ⋮ "save to folder" list).
    loadFolders() {
      var self = this;
      return httpClient.get("/report/team-email-folders")
        .then(function (res) { self.folders = (res.data && res.data.folders) || []; })
        .catch(function () { /* folders not set up yet — non-fatal */ });
    },
    selectFolder(f) {
      if (this.activeFolder === f) return;
      this.activeFolder = f;
      this.loadTeam();
    },
    // ── Per-email ⋮ menu ──
    openRowMenu(e, ev) {
      var w = 220, r = (ev.currentTarget && ev.currentTarget.getBoundingClientRect) ? ev.currentTarget.getBoundingClientRect() : { left: ev.clientX, right: ev.clientX, top: ev.clientY, bottom: ev.clientY };
      var x = Math.max(8, Math.min(r.right - w, (window.innerWidth || 1200) - w - 8));
      var y = r.bottom + 6;
      if (y > (window.innerHeight || 800) - 210) y = Math.max(8, r.top - 6 - 200);   // flip up near the bottom
      this.rowMenu = { open: true, email: e, emails: null, groupName: "", x: x, y: y, view: "root", newName: "", busy: false };
      if (!this.folders.length) this.loadFolders();
    },
    closeRowMenu() { if (!this.rowMenu.busy) this.rowMenu.open = false; },
    menuGoFolders() {
      this.rowMenu.view = "folders";
      if (!this.folders.length) this.loadFolders();
    },
    menuGoNewFolder() {
      this.rowMenu.view = "newfolder"; this.rowMenu.newName = "";
      var self = this;
      this.$nextTick(function () { try { self.$refs.newFolderInput && self.$refs.newFolderInput.focus(); } catch (e) { /* noop */ } });
    },
    // The email(s) the ⋮ menu acts on: a whole customer group (rowMenu.emails) or a single email (rowMenu.email).
    menuTargetIds() {
      var t = (this.rowMenu.emails && this.rowMenu.emails.length) ? this.rowMenu.emails : (this.rowMenu.email ? [this.rowMenu.email] : []);
      return t.map(function (e) { return e.id; });
    },
    // File the menu's email(s) into an existing folder.
    fileTo(folderId) {
      var self = this, ids = this.menuTargetIds();
      if (!ids.length) return;
      this.rowMenu.busy = true;
      Promise.all(ids.map(function (id) { return httpClient.post("/report/team-email-file", { InsightId: id, FolderId: folderId }); }))
        .then(function () { self.afterEmailAction("file", ids); })
        .catch(function () { self.rowMenu.busy = false; });
    },
    // Create a folder and file the current email(s) into it in one step.
    createAndFile() {
      var self = this, ids = this.menuTargetIds(), name = (this.rowMenu.newName || "").trim();
      if (!ids.length || !name || this.rowMenu.busy) return;
      this.rowMenu.busy = true;
      httpClient.post("/report/team-email-folders", { Name: name })
        .then(function (res) {
          var id = res.data && res.data.id;
          if (!id) { self.rowMenu.busy = false; return; }
          return Promise.all(ids.map(function (eid) { return httpClient.post("/report/team-email-file", { InsightId: eid, FolderId: id }); }))
            .then(function () { self.afterEmailAction("file", ids); });
        })
        .catch(function () { self.rowMenu.busy = false; });
    },
    // Soft-delete (hide) the menu's email(s) from this viewer's team inbox.
    doDelete() {
      var self = this, ids = this.menuTargetIds();
      if (!ids.length || this.rowMenu.busy) return;
      this.rowMenu.busy = true;
      Promise.all(ids.map(function (id) { return httpClient.post("/report/team-email-delete", { InsightId: id }); }))
        .then(function () { self.afterEmailAction("delete", ids); })
        .catch(function () { self.rowMenu.busy = false; });
    },
    // Shared cleanup after file/delete: close the menu, drop the affected row(s) locally (snappy), refresh counts.
    // Exception: filing while viewing "All" keeps the rows (they still belong there) — just reconcile the folder tags.
    afterEmailAction(kind, ids) {
      var idset = {}; (ids || []).forEach(function (id) { idset[id] = 1; });
      var keepInAll = (this.activeFolder === "all" && kind === "file");
      this.rowMenu.busy = false; this.rowMenu.open = false;
      if (ids && ids.length && !keepInAll) this.teamActive = this.teamActive.filter(function (r) { return !idset[r.id]; });
      this.loadFolders();
      if (keepInAll) this.loadTeam();
    },
    // Trash view: restore a deleted email back to the Inbox (un-delete + un-file). Reuses the file endpoint.
    restoreEmail(e) {
      if (!e) return;
      var self = this;
      httpClient.post("/report/team-email-file", { InsightId: e.id, FolderId: null })
        .then(function () {
          self.teamActive = self.teamActive.filter(function (r) { return r.id !== e.id; });   // leaves the Trash view
          self.loadFolders();
        })
        .catch(function () { /* non-fatal */ });
    },
    // ── Group (whole-customer) actions: apply an action to EVERY email in one customer group at once. ──
    // The emails behind a group row (teamActive filtered to that customer code).
    groupEmails(g) {
      if (!g) return [];
      var code = g.code;
      return (this.teamActive || []).filter(function (e) { return (e.customerCode || "(unmatched)") === code; });
    },
    // Open the ⋮ menu in GROUP mode (rowMenu.emails set) — Complete / Save-to-folder / Delete apply to all of them.
    openGroupMenu(g, ev) {
      var emails = this.groupEmails(g);
      if (!emails.length) return;
      var w = 240, r = (ev.currentTarget && ev.currentTarget.getBoundingClientRect) ? ev.currentTarget.getBoundingClientRect() : { left: ev.clientX, right: ev.clientX, top: ev.clientY, bottom: ev.clientY };
      var x = Math.max(8, Math.min(r.right - w, (window.innerWidth || 1200) - w - 8));
      var y = r.bottom + 6;
      if (y > (window.innerHeight || 800) - 230) y = Math.max(8, r.top - 6 - 220);   // flip up near the bottom
      this.rowMenu = { open: true, email: null, emails: emails, groupName: g.name + " (" + g.code + ")", x: x, y: y, view: "root", newName: "", busy: false };
      if (!this.folders.length) this.loadFolders();
    },
    // Menu "✅ Move (all) to Completed" — one email or the whole group (rowMenu.emails).
    menuComplete() {
      var self = this, ids = this.menuTargetIds();
      if (!ids.length) return;
      var idset = {}; ids.forEach(function (id) { idset[id] = 1; });
      this.rowMenu.busy = false; this.rowMenu.open = false;
      Promise.all(ids.map(function (id) { return httpClient.post("/report/team-email-complete", { InsightId: id }); }))
        .then(function () { self.teamActive = self.teamActive.filter(function (r) { return !idset[r.id]; }); })
        .catch(function () { self.teamError = "Couldn't move those to Completed. Please try again."; });
    },
    // Trash view: restore every email in a customer group back to the Inbox.
    restoreGroup(g) {
      var self = this, ids = this.groupEmails(g).map(function (e) { return e.id; });
      if (!ids.length) return;
      var idset = {}; ids.forEach(function (id) { idset[id] = 1; });
      Promise.all(ids.map(function (id) { return httpClient.post("/report/team-email-file", { InsightId: id, FolderId: null }); }))
        .then(function () { self.teamActive = self.teamActive.filter(function (r) { return !idset[r.id]; }); self.loadFolders(); })
        .catch(function () { /* non-fatal */ });
    },
    // Completed view: move every email in a customer group back to the Inbox as unread.
    reinboxGroup(g) {
      var self = this, ids = this.groupEmails(g).map(function (e) { return e.id; });
      if (!ids.length) return;
      var idset = {}; ids.forEach(function (id) { idset[id] = 1; });
      Promise.all(ids.map(function (id) { return httpClient.post("/report/team-email-reinbox", { InsightId: id }); }))
        .then(function () { self.teamActive = self.teamActive.filter(function (r) { return !idset[r.id]; }); })
        .catch(function () { self.teamError = "Couldn't move those back to the Inbox. Please try again."; });
    },
    // Header shortcuts (📁 Saved / 🗑 Trash next to the dock arrow, from CustomerInsights) → switch this card's view.
    onOpenView(v) {
      if (!this.isMaster && !this.ownGrouped) return;
      if (v === "settings") { if (!this.canAutoScan) return; this.loadScanSetting(); this.scanSettingsOpen = true; return; }
      if (v === "inbox" || v === "saved" || v === "deleted" || v === "completed") this.selectFolder(v);
    },
    // Grouped-view ↻: Master/Sales All rescan all reps (server scan) + reload; a regular Sales rep scans their own inbox.
    onGroupRefresh() { if (this.isMaster) this.refreshTeam(); else this.scanInbox(false); },
    refreshTeam() {
      var self = this;
      if (this.refreshing) return;
      this.refreshing = true;
      httpClient.post("/report/team-email-insights/refresh", {})
        .catch(function () { /* non-fatal — the scheduled scan still runs */ })
        .finally(function () {
          // Reload now (picks up anything already scanned), then again after the background rescan has a moment.
          self.loadTeam();
          setTimeout(function () { self.loadTeam(); self.refreshing = false; }, 5000);
        });
    },
    // ── Master-controlled recurring auto-scan (server-side TeamInboxScanner) ──
    loadScanSetting() {
      var self = this;
      return httpClient.get("/report/team-scan-setting")
        .then(function (res) {
          var d = res.data || {};
          self.autoScan.enabled = !!d.enabled;
          self.autoScan.intervalMinutes = self.snapInterval(d.intervalMinutes);
          self.autoScan.graphEnabled = !!d.graphEnabled;
        })
        .catch(function () { /* setting table not present yet — leave defaults (off) */ });
    },
    // Persist the on/off + interval. Interval is always a 15-minute multiple; the server clamps too.
    setAutoScan(enabled, intervalMinutes) {
      var self = this, mins = this.snapInterval(intervalMinutes);
      // optimistic
      this.autoScan.enabled = !!enabled;
      this.autoScan.intervalMinutes = mins;
      this.autoScan.busy = true;
      httpClient.post("/report/team-scan-setting", { Enabled: !!enabled, IntervalMinutes: mins })
        .then(function (res) {
          var d = res.data || {};
          if (typeof d.enabled === "boolean") self.autoScan.enabled = d.enabled;
          if (d.intervalMinutes) self.autoScan.intervalMinutes = self.snapInterval(d.intervalMinutes);
        })
        .catch(function () { /* keep the optimistic value; the next load reconciles */ })
        .finally(function () { self.autoScan.busy = false; });
    },
    // Round a minute value to the nearest valid 15-minute block within [15, 480].
    snapInterval(m) {
      var n = Math.round((Number(m) || 15) / 15) * 15;
      if (n < 15) n = 15;
      if (n > 480) n = 480;
      return n;
    },
    // "15 min", "45 min", "1 hr", "1 hr 15 min", … for the interval <select>.
    fmtInterval(mins) {
      var m = Number(mins) || 0, h = Math.floor(m / 60), r = m % 60;
      if (h === 0) return r + " min";
      var hs = h + " hr";
      return r === 0 ? hs : (hs + " " + r + " min");
    },
    // Move a team-inbox email into the green Completed view (out of the Inbox, marked read).
    completeTeamEmail(e) {
      if (!e) return;
      var self = this;
      if (this.rowMenu.open) { this.rowMenu.busy = false; this.rowMenu.open = false; }
      httpClient.post("/report/team-email-complete", { InsightId: e.id })
        .then(function () {
          self.teamActive = self.teamActive.filter(function (r) { return r.id !== e.id; });   // leaves the current view
        })
        .catch(function () { self.teamError = "Couldn't move that to Completed. Please try again."; });
    },
    // From the Completed view: move an email back to the Inbox as unread (bold).
    reinboxTeamEmail(e) {
      if (!e) return;
      var self = this;
      httpClient.post("/report/team-email-reinbox", { InsightId: e.id })
        .then(function () {
          self.teamActive = self.teamActive.filter(function (r) { return r.id !== e.id; });   // leaves the Completed view
        })
        .catch(function () { self.teamError = "Couldn't move that back to the Inbox. Please try again."; });
    },
    openDetail(e) {
      if (!e) return;
      this.detail = e; this.detailBody = ""; this.detailNotation = ""; this.detailSpecs = ""; this.noteOpen = false; this.specOpen = false; this.detailPersp = { open: false, busy: false, headline: "", body: "", actions: [] }; this.detailLoading = true;
      this.detailSuggestions = this.parseSuggestions(e.suggestions, e);   // from the list row (instant); refined on fetch
      var self = this;
      httpClient.get("/report/team-email-insights/" + e.id)
        .then(function (res) {
          self.detailBody = (res.data && res.data.body) || "";
          self.detailNotation = (res.data && res.data.customerNotation) || "";
          self.detailSpecs = (res.data && res.data.customerSpecs) || "";
          self.detailSuggestions = self.parseSuggestions(res.data && res.data.suggestions, e);
        })
        .catch(function () { self.detailBody = e.preview || "(couldn't load the message)"; })
        .finally(function () { self.detailLoading = false; });
    },
    // Ranked suggestions for an email: prefer the stored JSON list; fall back to the single top-pick columns
    // (rows scanned before the multi-suggestion column existed). An EXPLICIT empty list ("[]") means the server
    // filtered every suggestion out (none of the parts sit on a machine linked to the account) — honor it and
    // do NOT resurrect the top pick from the row columns.
    parseSuggestions(json, e) {
      var arr = [], explicit = false;
      if (json) {
        try {
          var p = JSON.parse(json);
          if (Array.isArray(p)) { arr = p; explicit = true; }
        } catch (x) { arr = []; }
      }
      if (!explicit && (!arr || !arr.length) && e && e.partNumber) {
        arr = [{ partNumber: e.partNumber, partDescription: e.partDescription, machine: e.machine, machineName: e.machineName, assembly: e.assembly, assemblyId: e.assemblyId, avgQty: e.avgQty, probability: e.probability, confidence: e.confidence, reason: e.reason }];
      }
      return arr || [];
    },
    // Open a quote for one suggestion — merge in the open email's customer fields (which live on `detail`, not
    // on the per-part suggestion object) so the new tab switches into the right customer company.
    openQuoteFor(s) {
      if (!s || !this.detail) return;
      var d = this.detail;
      this.openQuote(Object.assign({}, s, { customerCode: d.customerCode, customerName: d.customerName, companyId: d.companyId }));
    },
    closeDetail() { this.detail = null; this.detailBody = ""; this.detailNotation = ""; this.detailSpecs = ""; this.detailPersp = { open: false, busy: false, headline: "", body: "", actions: [] }; this.detailSuggestions = []; },
    // 💡 On-demand AI sales perspective on the matched customer's note + specs (inline in the email detail modal).
    loadDetailPersp() {
      var d = this.detail; if (!d || !d.customerCode) return;
      if (this.detailPersp.open && !this.detailPersp.busy && (this.detailPersp.body || this.detailPersp.headline)) { this.detailPersp.open = false; return; }
      this.detailPersp = { open: true, busy: true, headline: "", body: "", actions: [] };
      var self = this;
      httpClient.post("/report/notation-perspective", { customerCode: d.customerCode, level: "customer" })
        .then(function (res) {
          var r = res && res.data, pv = r && r.perspective;
          if (r && r.ok && pv) {
            self.detailPersp.headline = pv.headline || "";
            self.detailPersp.body = pv.body || "";
            self.detailPersp.actions = Array.isArray(pv.actions) ? pv.actions : [];
          } else { self.detailPersp.body = (r && r.message) || "No perspective available for this customer yet."; }
        })
        .catch(function () { self.detailPersp.body = "Couldn't load a perspective right now — please try again."; })
        .finally(function () { self.detailPersp.busy = false; });
    },
    // Step to the newer (‹) / older (›) email from the same customer without closing the modal.
    goDetailPrev() { var i = this.detailIndex; if (i > 0) this.openDetail(this.emailListEmails[i - 1]); },
    goDetailNext() { var i = this.detailIndex, list = this.emailListEmails || []; if (i >= 0 && i < list.length - 1) this.openDetail(list[i + 1]); },
    openReply(e) {
      if (!e) return;
      var opts = this.replyRecipientOptions(e);
      var defTo = e.fromAddress || (opts[0] && opts[0].address) || e.repEmail || "";
      this.reply = { open: true, insightId: e.id, repEmail: e.repEmail || "", toList: defTo ? [defTo] : [], cc: "", toOptions: opts, subject: "RE: " + (e.subject || ""), body: "", busy: false, msg: "" };
    },
    // To-picker actions: each pick APPENDS a chip (and leaves the dropdown); "All recipients" adds everyone at once.
    onAddSelect(ev) {
      var v = ev && ev.target ? ev.target.value : "";
      if (v) this.addTo(v);
      if (ev && ev.target) ev.target.value = "";   // reset back to the "+ Add recipient…" placeholder
    },
    addTo(v) {
      if (!v) return;
      var self = this;
      if (v === "__ALL__") { this.toAvailable.forEach(function (o) { if (self.reply.toList.indexOf(o.address) < 0) self.reply.toList.push(o.address); }); return; }
      if (this.reply.toList.indexOf(v) < 0) this.reply.toList.push(v);
    },
    removeTo(a) { var i = this.reply.toList.indexOf(a); if (i >= 0) this.reply.toList.splice(i, 1); },
    toChipLabel(a) {
      var o = (this.reply.toOptions || []).find(function (x) { return x.address === a; });
      return (o && o.name) ? o.name : a;
    },
    // Recipients for the Reply "To" dropdown: everyone on the customer's email chain (From/To/CC captured at
    // scan time) + each email's sender + the assigned rep. Deduped by address; the open email's sender first.
    replyRecipientOptions(email) {
      var code = email.customerCode || "(unmatched)";
      var group = (this.teamActive || []).filter(function (r) { return (r.customerCode || "(unmatched)") === code; });
      if (!group.length) group = [email];
      var seen = {}, out = [];
      function add(name, addr) {
        if (!addr) return;
        var a = String(addr).trim(); if (!a || a.indexOf("@") < 0) return;
        var key = a.toLowerCase(); if (seen[key]) return; seen[key] = 1;
        var nm = (name && String(name).trim() && String(name).trim().toLowerCase() !== key) ? String(name).trim() : "";
        out.push({ name: nm, address: a, label: nm ? (nm + " <" + a + ">") : a });
      }
      add(email.fromName, email.fromAddress);   // the open email's sender first (natural default)
      group.forEach(function (r) {
        add(r.fromName, r.fromAddress);
        add(r.repName, r.repEmail);
        var parts = [];
        try { parts = r.participants ? (typeof r.participants === "string" ? JSON.parse(r.participants) : r.participants) : []; } catch (x) { parts = []; }
        (parts || []).forEach(function (p) { add(p.n || p.name, p.a || p.address); });
      });
      return out;
    },
    closeReply() { if (!this.reply.busy) this.reply.open = false; },
    sendReply() {
      var self = this, r = this.reply;
      if (!r.toList.length || !r.subject || !r.body || r.busy) return;
      r.busy = true; r.msg = "";
      httpClient.post("/report/team-email-reply", { InsightId: r.insightId, To: r.toList.join(";"), Cc: r.cc, Subject: r.subject, Body: r.body })
        .then(function (res) {
          if (res.data && res.data.ok) { self.reply.msg = "Sent to " + (res.data.sentTo || "the recipient") + "."; setTimeout(function () { self.reply.open = false; }, 1200); }
          else self.reply.msg = (res.data && res.data.message) || "Couldn't send the reply.";
        })
        .catch(function () { self.reply.msg = "Couldn't send the reply. Please try again."; })
        .finally(function () { self.reply.busy = false; });
    },
    // Register (Active) or clear (unlink) this rep's opt-in for server-side team-inbox scanning. Best-effort.
    optin(active) {
      httpClient.post("/user/email-optin", { OptedIn: !!active }).catch(function () { /* non-fatal */ });
    },
    // Reduce an HTML (or plain) body to readable plain text — safe to persist + render as text (no XSS).
    _plain(s) {
      if (!s) return s;
      var t = String(s)
        .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n")
        .replace(/<[^>]+>/g, " ");
      try { var el = document.createElement("textarea"); el.innerHTML = t; t = el.value; } catch (e) { /* noop */ }
      return t.replace(/[ \t\f\v]+/g, " ").replace(/\s*\n\s*/g, "\n").trim().slice(0, 20000);
    },
    _msg(err) {
      var s = err && err.message ? String(err.message) : "";
      if (/popup|user_cancel|cancell?ed|window_closed/i.test(s)) return "Sign-in was cancelled.";
      if (/consent|interaction_required|Mail\.Read/i.test(s)) return "Email access wasn't granted. Ask IT to confirm the Mail.Read permission, then try again.";
      return "Couldn't read your email. " + (s || "Please try again.");
    },
  },
};
</script>

<style scoped>
.ei-wrap { font-size: 12.5px; color: #333; height: 100%; display: flex; flex-direction: column; min-height: 0; }
/* Opt-in */
.ei-optin { display: flex; align-items: center; gap: 12px; padding: 10px 4px; flex-wrap: wrap; }
.ei-optin-ico { font-size: 26px; }
.ei-optin-txt { flex: 1 1 220px; min-width: 200px; }
.ei-optin-h { font-weight: 700; font-size: 13.5px; margin-bottom: 2px; }
.ei-optin-txt p { margin: 0; color: #666; line-height: 1.45; font-size: 12px; }
/* Bar */
.ei-bar { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.ei-bar-lbl { font-weight: 700; font-size: 12.5px; }
.ei-bar-lbl em { color: #888; font-style: normal; font-weight: 400; }
.ei-unlink { margin-left: auto; }
/* Buttons */
.ei-btn { border: 1px solid #ccc; background: #fff; border-radius: 6px; padding: 5px 10px; font-size: 12px; cursor: pointer; color: #333; }
.ei-btn:hover { background: #f5f5f5; }
.ei-btn-primary { background: #9e2436; color: #fff; border-color: #000; font-weight: 600; padding: 7px 14px; }
.ei-btn-primary:hover { background: #86202f; }
.ei-btn:disabled { opacity: .6; cursor: default; }
.ei-btn-sm { padding: 3px 8px; font-size: 11px; }
/* Body / list */
.ei-linked { display: flex; flex-direction: column; min-height: 0; flex: 1 1 auto; }
.ei-body { display: flex; flex-direction: column; min-height: 0; flex: 1 1 auto; overflow-y: auto; }
.ei-list { list-style: none; margin: 0; padding: 0; }
.ei-item { border: 1px solid #eaeaec; border-left: 3px solid #9e2436; border-radius: 6px; padding: 8px 10px; margin-bottom: 8px; background: #fff; }
.ei-item-top { display: flex; align-items: baseline; gap: 8px; }
.ei-cust { font-weight: 700; color: #9e2436; font-size: 12.5px; }
.ei-cust em { color: #888; font-style: normal; font-weight: 400; font-size: 11px; }
.ei-date { margin-left: auto; color: #999; font-size: 11px; white-space: nowrap; }
.ei-subj { font-weight: 600; margin-top: 2px; }
.ei-from { color: #777; font-size: 11.5px; }
.ei-prev { color: #666; font-size: 11.5px; margin-top: 3px; max-height: 34px; overflow: hidden; line-height: 1.35; }
.ei-actions { margin-top: 7px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ei-complete { margin-left: auto; color: #1e7d34; border-color: #bcdcc4; }
.ei-complete:hover { background: #edf7f0; }
/* Dark mode — the list rows were white (.ei-item background:#fff) with dark text; darken the card + lighten text. */
html.dark-mode .ei-item { background: #1f242b; border-color: #333a42; border-left-color: #9e2436; }
html.dark-mode .ei-cust { color: #f0a8b1; }
html.dark-mode .ei-cust em { color: #9aa3ad; }
html.dark-mode .ei-date { color: #8a929c; }
html.dark-mode .ei-subj { color: #dfe3e8; }
html.dark-mode .ei-from { color: #9aa3ad; }
html.dark-mode .ei-prev { color: #aab0b8; }
html.dark-mode .ei-rep { color: #aab0b8; }
html.dark-mode .ei-team-item:hover, html.dark-mode .ei-team-item.is-menu { background: #2a313a; border-left-color: #86202f; }
.ei-part-chip { border: 1px solid #000; background: #9e2436; color: #fff; border-radius: 6px; padding: 5px 10px; font-size: 12px; font-weight: 600; cursor: pointer; text-align: left; }
.ei-part-chip:hover { background: #86202f; }
.ei-part-chip-sm { padding: 3px 8px; font-size: 11px; }
.ei-part-desc { font-weight: 400; opacity: .9; }
.ei-conf { font-style: normal; font-size: 10px; text-transform: uppercase; letter-spacing: .5px; margin-left: 6px; padding: 1px 5px; border-radius: 4px; background: rgba(255,255,255,.25); }
.ei-conf.is-high { background: #1e7d34; }
.ei-conf.is-medium { background: #b8860b; }
.ei-conf.is-low { background: #777; }
.ei-nomatch { color: #999; font-size: 11.5px; }
/* Completed section */
.ei-completed { margin-top: 4px; border-top: 1px solid #eee; padding-top: 8px; }
.ei-completed-hdr { border: none; background: none; color: #666; font-size: 12px; font-weight: 700; cursor: pointer; padding: 2px 0; }
.ei-completed-hdr:hover { color: #333; }
.ei-caret { display: inline-block; width: 12px; color: #999; }
.ei-list-done .ei-item.is-done { border-left-color: #bcdcc4; background: #fafafa; opacity: .9; }
.ei-list-done .ei-cust { color: #6b6b6b; }
.ei-reopen { color: #9e2436; }
/* States */
.ei-loading, .ei-empty { color: #888; font-size: 12px; padding: 10px 2px; }
.ei-scan-cta { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; }
.ei-scan-cta p { margin: 0; }
.ei-note { color: #b8860b; font-size: 11px; }
.ei-err { color: #b3261e; font-size: 12px; margin-top: 6px; }
/* Spinner */
.ei-spin { display: inline-block; width: 11px; height: 11px; border: 2px solid rgba(0,0,0,.2); border-top-color: #9e2436; border-radius: 50%; margin-right: 5px; vertical-align: -1px; animation: ei-spin .7s linear infinite; }
.ei-btn-primary .ei-spin { border-color: rgba(255,255,255,.4); border-top-color: #fff; }
@keyframes ei-spin { to { transform: rotate(360deg); } }
/* ── Master team-inbox: centered title + right-aligned actions ── */
.ei-bar-team { position: relative; display: block; text-align: center; min-height: 24px; }
.ei-bar-team .ei-bar-lbl { display: inline-block; line-height: 24px; }
.ei-bar-team .ei-bar-actions { position: absolute; right: 0; top: 0; height: 24px; display: flex; align-items: center; gap: 6px; }
/* ── Folder chips ── */
.ei-folders { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 8px; }
.ei-fchip { border: 1px solid #e2c9ce; background: #fff; color: #6a2b34; border-radius: 999px; padding: 2px 9px; font-size: 11px; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; }
.ei-fchip em { font-style: normal; color: #a0264a; background: #f4e3e6; border-radius: 999px; padding: 0 5px; font-size: 10px; }
.ei-fchip:hover { background: #faf3f4; }
.ei-fchip.is-on { background: #9e2436; border-color: #9e2436; color: #fff; }
.ei-fchip.is-on em { background: rgba(255,255,255,.25); color: #fff; }
.ei-fchip-all { color: #555; border-color: #d9d9de; }
/* Active-view label (shown for Saved / Trash, since those aren't the default Inbox) */
.ei-viewlbl { font-size: 11px; font-weight: 700; color: #7a2530; margin: -2px 0 7px; }
html.dark-mode .ei-viewlbl { color: #f0b6be; }
/* ── Master team-inbox rows ── */
.ei-team-item { cursor: pointer; position: relative; padding-right: 24px; }
.ei-team-item:hover { background: #faf3f4; border-left-color: #86202f; }
.ei-team-item.is-menu { background: #faf3f4; border-left-color: #86202f; }
.ei-rep { margin-left: auto; color: #4a4a4a; font-size: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 46%; }
.ei-team-item .ei-date { margin-left: 8px; }
.ei-kebab { position: absolute; top: 50%; right: 3px; transform: translateY(-50%); border: 0; background: none; color: #b0b0b6; font-size: 17px; line-height: 1; cursor: pointer; padding: 2px 4px; border-radius: 5px; }
.ei-kebab:hover { color: #9e2436; background: #f3e7e9; }
.ei-restore { color: #2a7d46; font-size: 15px; font-weight: 700; }
.ei-restore:hover { color: #1e5c33; background: #e7f4ec; }
.ei-folder-tag { margin-left: 6px; color: #a0264a; font-size: 10.5px; white-space: nowrap; }
.ei-fchip-trash { color: #8a4a4a; }
.ei-fchip-trash.is-on { background: #b12a3a; border-color: #b12a3a; color: #fff; }
/* ── Per-email ⋮ actions menu (portaled to the report root) ── */
.ei-menu-back { position: fixed; inset: 0; z-index: 10072; background: transparent; }
.ei-menu { position: fixed; z-index: 10073; width: 220px; max-width: 92vw; background: #fff; color: #222; border: 1px solid #e2e2e6; border-radius: 10px; box-shadow: 0 10px 34px rgba(0,0,0,.24); padding: 6px; font-size: 12.5px; }
.ei-menu-hd { font-weight: 700; color: #7a2530; padding: 4px 8px 6px; border-bottom: 1px solid #f0f0f2; margin-bottom: 4px; display: flex; align-items: center; gap: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ei-menu-crumb { border: 0; background: none; color: #9e2436; font-size: 15px; line-height: 1; cursor: pointer; padding: 0 4px; }
.ei-menu-item { display: flex; align-items: center; gap: 6px; width: 100%; text-align: left; border: 0; background: none; color: #333; padding: 7px 8px; border-radius: 6px; cursor: pointer; font-size: 12.5px; }
.ei-menu-item:hover { background: #f5eef0; }
.ei-menu-item:disabled { opacity: .5; cursor: default; }
.ei-menu-del { color: #b12a3a; }
.ei-menu-del:hover { background: #fbeaec; }
.ei-menu-new { color: #2a7d46; border-top: 1px solid #f0f0f2; margin-top: 4px; }
.ei-menu-go { justify-content: center; background: #9e2436; color: #fff; margin-top: 4px; }
.ei-menu-go:hover:not(:disabled) { background: #86202f; }
.ei-menu-scroll { max-height: 180px; overflow: auto; }
.ei-menu-empty { color: #999; font-style: italic; padding: 6px 8px; font-size: 11.5px; }
.ei-menu-ct { margin-left: auto; font-style: normal; color: #a0264a; background: #f4e3e6; border-radius: 999px; padding: 0 6px; font-size: 10.5px; }
.ei-menu-input { width: 100%; box-sizing: border-box; border: 1px solid #d4d4d8; border-radius: 6px; padding: 6px 8px; font-size: 13px; margin: 2px 0 4px; }
.ei-menu-note { font-size: 11px; color: #888; padding: 0 8px 6px; }
.ei-menu-row { display: flex; gap: 6px; }
.ei-menu-row .ei-menu-item { justify-content: center; border: 1px solid #e2e2e6; }
/* ── Detail / Reply modals (portaled to the report root) ── */
.ei-modal-back { position: fixed; inset: 0; background: rgba(0,0,0,.42); z-index: 10070; }
.ei-modal { position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%); width: min(640px, 94vw); max-height: 88vh; overflow: auto; background: #fff; color: #222; border-radius: 10px; box-shadow: 0 12px 44px rgba(0,0,0,.32); z-index: 10071; display: flex; flex-direction: column; font-size: 13px; }
.ei-modal-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 12px 16px; border-bottom: 1px solid #eee; border-top: 3px solid #9e2436; border-radius: 10px 10px 0 0; }
.ei-modal-title { font-weight: 800; color: #9e2436; font-size: 14px; }
.ei-modal-title em { color: #888; font-style: normal; font-weight: 400; font-size: 12px; }
.ei-modal-x { border: 0; background: none; font-size: 22px; line-height: 1; color: #999; cursor: pointer; padding: 0 2px; }
.ei-modal-x:hover { color: #9e2436; }
.ei-modal-meta { padding: 10px 16px; display: grid; grid-template-columns: 1fr 1fr; gap: 3px 16px; font-size: 12px; color: #444; border-bottom: 1px solid #f2f2f2; }
.ei-modal-meta b { color: #666; font-weight: 700; }
.ei-modal-subj { grid-column: 1 / -1; }
.ei-modal-body { padding: 12px 16px; flex: 1 1 auto; min-height: 60px; }
.ei-modal-text { white-space: pre-wrap; word-break: break-word; font-family: inherit; font-size: 12.5px; color: #333; margin: 0; max-height: 42vh; overflow: auto; line-height: 1.45; }
.ei-modal-ai { padding: 10px 16px; border-top: 1px solid #f2f2f2; display: flex; flex-direction: column; align-items: flex-start; gap: 6px; }
.ei-ai-h { font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #888; font-weight: 700; }
.ei-ai-h em { text-transform: none; letter-spacing: 0; font-style: normal; font-weight: 400; color: #aaa; }
.ei-ai-reason { font-size: 11.5px; color: #666; font-style: italic; }
.ei-ai-sugg { display: flex; flex-direction: column; align-items: flex-start; gap: 3px; width: 100%; padding: 6px 0; border-top: 1px solid #f1f1f3; }
.ei-ai-sugg:first-of-type { border-top: 0; padding-top: 2px; }
.ei-ai-ctx { font-size: 11px; color: #8a8a8a; }
html.dark-mode .ei-ai-sugg { border-top-color: #333a42; }
html.dark-mode .ei-ai-ctx { color: #9aa3ad; }
.ei-modal-foot { padding: 12px 16px; border-top: 1px solid #eee; display: flex; justify-content: flex-end; gap: 8px; }
/* Prev/next carousel arrows on the detail modal (red circle + white chevron). */
.ei-nav-arrow { position: absolute; top: 50%; transform: translateY(-50%); width: 36px; height: 36px; padding: 0; border: 0; border-radius: 50%; background: #9e2436; color: #fff; font-size: 24px; line-height: 34px; text-align: center; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,.35); z-index: 3; }
.ei-nav-arrow:hover { background: #86202f; }
.ei-nav-prev { left: -18px; }
.ei-nav-next { right: -18px; }
/* Detail modal: let the arrows overhang the edges (scroll the body instead of the whole modal). */
.ei-modal-detail { overflow: visible; }
.ei-modal-detail .ei-modal-body { overflow-y: auto; min-height: 0; }
.ei-lbl-opt { color: #999; font-weight: 400; font-size: 11px; }
.ei-reply-hint { margin-top: 8px; font-size: 11px; color: #888; }
html.dark-mode .ei-lbl-opt, html.dark-mode .ei-reply-hint { color: #8a929c; }
/* Reply "To" as a multi-recipient chip box + add-picker. */
.ei-to-box { display: flex; flex-wrap: wrap; gap: 5px; align-items: center; border: 1px solid #d4d4d8; border-radius: 6px; padding: 5px 6px; min-height: 34px; box-sizing: border-box; }
.ei-to-chip { display: inline-flex; align-items: center; gap: 4px; background: #f1e4e6; color: #7a1f2b; border: 1px solid #e6c9ce; border-radius: 12px; padding: 2px 6px 2px 9px; font-size: 12px; font-weight: 600; max-width: 100%; }
.ei-to-x { border: 0; background: none; color: #9e2436; font-size: 15px; line-height: 1; cursor: pointer; padding: 0 1px; }
.ei-to-add { border: 0; background: none; font-size: 12px; color: #444; cursor: pointer; outline: none; padding: 2px; max-width: 100%; }
.ei-to-empty { color: #b0b0b6; font-size: 12px; }
html.dark-mode .ei-to-box { border-color: #3a414a; background: #11151a; }
html.dark-mode .ei-to-chip { background: #3a2a2e; border-color: #6e3a40; color: #f0a8b1; }
html.dark-mode .ei-to-add { color: #cdd3da; }
html.dark-mode .ei-to-empty { color: #6a727c; }
.ei-notation { margin: 0 16px 10px; border: 1px solid #f0d69a; border-left: 3px solid #d9a441; background: #fdf6e3; border-radius: 6px; padding: 8px 10px; }
.ei-notation-lbl { font-weight: 800; color: #8a6d1a; font-size: 11px; }
.ei-notation-toggle { display: flex; align-items: center; gap: 5px; width: 100%; border: 0; background: none; cursor: pointer; text-align: left; padding: 0; font: inherit; }
.ei-notation-caret { font-size: 9px; opacity: .7; }
.ei-notation-txt { white-space: pre-wrap; word-break: break-word; color: #5a4a1e; margin-top: 3px; font-size: 12.5px; line-height: 1.4; }
html.dark-mode .ei-notation { background: #2c2617; border-color: #5a4a1e; border-left-color: #d9a441; }
html.dark-mode .ei-notation-lbl { color: #e6c65f; }
html.dark-mode .ei-notation-txt { color: #cbbfa4; }
/* 📋 specs = blue variant of the note block; 💡 inline AI perspective (amber). */
.ei-spec { border-color: #bcd6ef; border-left-color: #2f6fb0; background: #eef5fc; }
.ei-spec .ei-notation-lbl { color: #245a91; }
.ei-spec .ei-notation-txt { color: #274a63; }
.ei-persp-wrap { margin: 0 16px 10px; }
.ei-persp-btn { border: 1px solid #e0c48a; background: #fff8e8; color: #8a6d1a; font-weight: 700; font-size: 12px; border-radius: 6px; padding: 4px 10px; cursor: pointer; }
.ei-persp-btn:hover { background: #fdefc8; }
.ei-persp-btn:disabled { opacity: .6; cursor: default; }
.ei-persp { margin-top: 8px; border: 1px solid #f0d69a; border-left: 3px solid #d9a441; background: #fdf6e3; border-radius: 6px; padding: 8px 10px; }
.ei-persp-headline { font-weight: 800; color: #8a6d1a; font-size: 12.5px; margin-bottom: 3px; }
.ei-persp-body { white-space: pre-wrap; word-break: break-word; color: #5a4a1e; font-size: 12.5px; line-height: 1.45; }
.ei-persp-actions { margin: 8px 0 0; padding-left: 18px; }
.ei-persp-actions li { font-size: 12px; line-height: 1.4; color: #5a4a1e; margin-bottom: 3px; }
.ei-persp-loading { font-size: 12.5px; color: #777; display: flex; align-items: center; gap: 8px; }
.ei-persp-spin { width: 13px; height: 13px; border: 2px solid #e0c48a; border-top-color: #d9a441; border-radius: 50%; display: inline-block; animation: ei-persp-spin 0.7s linear infinite; }
@keyframes ei-persp-spin { to { transform: rotate(360deg); } }
html.dark-mode .ei-spec { background: #16222c; border-color: #2f4a63; border-left-color: #4a90d9; }
html.dark-mode .ei-spec .ei-notation-lbl { color: #8fc0ef; }
html.dark-mode .ei-spec .ei-notation-txt { color: #b9d4ea; }
html.dark-mode .ei-persp-btn { background: #2c2617; border-color: #5a4a1e; color: #e6c65f; }
html.dark-mode .ei-persp { background: #2c2617; border-color: #5a4a1e; border-left-color: #d9a441; }
html.dark-mode .ei-persp-headline { color: #e6c65f; }
html.dark-mode .ei-persp-body, html.dark-mode .ei-persp-actions li { color: #cbbfa4; }
.ei-modal-reply .ei-modal-body { display: flex; flex-direction: column; gap: 3px; }
.ei-lbl { font-size: 11px; font-weight: 700; color: #777; margin-top: 6px; }
.ei-input { border: 1px solid #d4d4d8; border-radius: 6px; padding: 6px 8px; font-size: 13px; width: 100%; box-sizing: border-box; }
.ei-input[readonly] { background: #f6f6f7; color: #666; }
.ei-textarea { resize: vertical; font-family: inherit; }
/* Dark mode (the modals are portaled but keep the component scope id, so these still match). */
html.dark-mode .ei-modal { background: #1f242b; color: #e3e7eb; }
html.dark-mode .ei-modal-meta { color: #c7cdd4; border-bottom-color: #333a42; }
html.dark-mode .ei-modal-meta b { color: #9aa3ad; }
html.dark-mode .ei-modal-text { color: #cdd3da; }
html.dark-mode .ei-modal-head { border-bottom-color: #333a42; }
html.dark-mode .ei-modal-body, html.dark-mode .ei-modal-ai, html.dark-mode .ei-modal-foot { border-color: #333a42; }
html.dark-mode .ei-input { background: #11151a; border-color: #3a414a; color: #e3e7eb; }
html.dark-mode .ei-input[readonly] { background: #171c22; color: #9aa3ad; }
/* Dark mode — folder chips + ⋮ menu */
html.dark-mode .ei-fchip { background: #1f242b; border-color: #3a414a; color: #e7c3c9; }
html.dark-mode .ei-fchip.is-on { background: #9e2436; border-color: #9e2436; color: #fff; }
html.dark-mode .ei-fchip em { background: #2a313a; color: #e7c3c9; }
html.dark-mode .ei-fchip-all { color: #aeb5bd; }
html.dark-mode .ei-kebab:hover { background: #2a313a; }
html.dark-mode .ei-menu { background: #1f242b; color: #e3e7eb; border-color: #333a42; }
html.dark-mode .ei-menu-hd { color: #f0b6be; border-bottom-color: #333a42; }
html.dark-mode .ei-menu-item { color: #dfe3e8; }
html.dark-mode .ei-menu-item:hover { background: #2a313a; }
html.dark-mode .ei-menu-new { border-top-color: #333a42; }
html.dark-mode .ei-menu-input { background: #11151a; border-color: #3a414a; color: #e3e7eb; }
html.dark-mode .ei-menu-ct { background: #2a313a; color: #e7c3c9; }
html.dark-mode .ei-menu-row .ei-menu-item { border-color: #3a414a; }
html.dark-mode .ei-folder-tag { color: #e7c3c9; }
html.dark-mode .ei-restore { color: #6fd699; }
html.dark-mode .ei-restore:hover { color: #8fe6b3; background: #223026; }
html.dark-mode .ei-fchip-trash { color: #d59aa0; }
/* ── Master auto-scan control (on/off + 15-min-block interval) ── */
.ei-autoscan { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin: 2px 0 8px; font-size: 11.5px; color: #555; }
.ei-autoscan-tog { display: inline-flex; align-items: center; gap: 5px; cursor: pointer; font-weight: 600; color: #4a4a4a; }
.ei-autoscan-tog input { cursor: pointer; }
.ei-autoscan-int { border: 1px solid #d4d4d8; border-radius: 6px; padding: 2px 6px; font-size: 11.5px; background: #fff; color: #333; cursor: pointer; }
.ei-autoscan-int:disabled { opacity: .5; cursor: default; }
.ei-autoscan-note { color: #b8860b; font-size: 10.5px; }
html.dark-mode .ei-autoscan { color: #aeb5bd; }
html.dark-mode .ei-autoscan-tog { color: #cdd3da; }
html.dark-mode .ei-autoscan-int { background: #11151a; border-color: #3a414a; color: #e3e7eb; }
html.dark-mode .ei-autoscan-note { color: #d9b25a; }
/* ⚙ Auto-scan settings popup (centered modal; reuses .ei-modal-head / -title / -x) */
.ei-settings { position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%); width: min(400px, 92vw); background: #fff; color: #222; border-radius: 10px; box-shadow: 0 12px 44px rgba(0,0,0,.32); z-index: 10071; display: flex; flex-direction: column; font-size: 13px; }
.ei-settings-body { padding: 14px 16px 16px; }
.ei-settings-lead { margin: 0 0 12px; color: #555; font-size: 12.5px; line-height: 1.45; }
.ei-settings .ei-autoscan { margin: 0; font-size: 13px; }
.ei-settings .ei-autoscan-int { font-size: 13px; padding: 4px 8px; }
.ei-settings-note { margin-top: 12px; padding: 8px 10px; background: #fff7e6; border: 1px solid #f0d089; border-radius: 6px; color: #8a6100; font-size: 11.5px; line-height: 1.4; }
html.dark-mode .ei-settings { background: #1f242b; color: #e3e7eb; }
html.dark-mode .ei-settings-lead { color: #aeb5bd; }
html.dark-mode .ei-settings-note { background: #2a2410; border-color: #5a4a1a; color: #e0c67a; }
/* ── Unread (bold) rows — team inbox + rep inbox ── */
.ei-item.ei-unread .ei-subj { font-weight: 800; color: #1a1a1a; }
.ei-item.ei-unread .ei-cust { font-weight: 800; }
.ei-item.ei-unread { border-left-color: #9e2436; }
html.dark-mode .ei-item.ei-unread .ei-subj { color: #fff; }
/* ── Customer-grouped list + list modal + footer (customer grouping / view-all) ── */
.ei-group { cursor: pointer; position: relative; padding-right: 24px; }
.ei-group:hover { background: #faf3f4; }
.ei-group .ei-date { margin-left: auto; }
.ei-group-sub { color: #666; margin-top: 2px; }
.ei-group-count { font-weight: 700; color: #444; }
.ei-group-badge { font-size: 10px; font-weight: 700; color: #9e2436; background: #fdecee; border: 1px solid #f0c9cf; border-radius: 10px; padding: 1px 7px; white-space: nowrap; }
.ei-foot { flex: none; padding: 8px 2px 2px; margin-top: 6px; border-top: 1px solid #eee; }
.ei-viewall { width: 100%; justify-content: center; }
.ei-modal-list-back { position: fixed; inset: 0; background: rgba(0,0,0,.42); z-index: 10068; }
.ei-modal-list { position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%); width: min(640px,94vw); max-height: 88vh; overflow: hidden; background: #fff; color: #222; border-radius: 10px; box-shadow: 0 12px 44px rgba(0,0,0,.32); z-index: 10069; display: flex; flex-direction: column; font-size: 13px; }
.ei-modal-listbody { padding: 10px 14px 12px; overflow-y: auto; }
html.dark-mode .ei-group:hover { background: #2a313a; }
html.dark-mode .ei-group-sub { color: #aeb6bf; }
html.dark-mode .ei-group-count { color: #cdd3da; }
html.dark-mode .ei-group-badge { background: #3a2a2e; border-color: #6e3a40; color: #f0a8b1; }
html.dark-mode .ei-foot { border-top-color: #333a42; }
html.dark-mode .ei-modal-list { background: #1f242b; color: #e3e7eb; }
/* ── Green "Completed" folder ── */
.ei-viewlbl-done { color: #1e7d34; }
html.dark-mode .ei-viewlbl-done { color: #6fd699; }
.ei-completed-green { border-top-color: #bfe3c9; }
.ei-completed-green .ei-completed-hdr { color: #1e7d34; }
.ei-completed-green .ei-completed-hdr:hover { color: #155f27; }
.ei-completed-green .ei-caret { color: #6fbf85; }
html.dark-mode .ei-completed-green { border-top-color: #2c4a35; }
html.dark-mode .ei-completed-green .ei-completed-hdr { color: #6fd699; }
.ei-menu-done { color: #1e7d34; }
.ei-menu-done:hover { background: #e7f4ec; }
html.dark-mode .ei-menu-done { color: #6fd699; }
html.dark-mode .ei-menu-done:hover { background: #223026; }
</style>
